import React, { useState, FC, useMemo, useEffect } from 'react';
import { Table, Column } from '@appkit4/react-components/table';
import { Checkbox } from '@appkit4/react-components/checkbox';
import { Loading } from '@appkit4/react-components/loading';
import { Pagination } from '@appkit4/react-components/pagination';
import './ListingTable.scss';
import Tabs, { TabItem } from '../tabs/Tabs';
import { Search } from '@appkit4/react-components/search';

interface ListingTableProps {
    title?: string;
    subheading?: string;
    tabs?: TabItem[];
    activeTab?: number;
    columns: any[];
    data: any[];
    showSearch?: boolean;
    loading?: boolean;
    onTabChange?: any;
    // Pagination props
    totalCount: number;
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    // Table props
    condensed?: boolean;
    striped?: boolean;
    className?: string;
    headerButtons?: React.ReactNode;
    // Additional table props
    hasTitle?: boolean;

    onCheckChange?: (sortedData: Array<any>) => void;

    tableId?: string;
    onRowClick?: (event: MouseEvent, index: number, data: any) => void;
    selectedIndexs?: number[];

    onSelectedChange?: (selectedIds: number[]) => void;
    checkableColumn?: {
        field: string;
        header: string;
        renderCell?: (row: any) => React.ReactNode;
    };
}

const ListingTable: FC<ListingTableProps> = ({
    title,
    subheading,
    tabs,
    activeTab = 0,
    columns,
    data,
    showSearch = false,
    loading = false,
    onTabChange,
    currentPage,
    pageSize = 7,
    onPageChange,
    condensed = false,
    striped = false,
    className,
    headerButtons,
    hasTitle = true,

    onCheckChange,

    tableId,
    onRowClick,
    selectedIndexs = [],
    onSelectedChange,
    checkableColumn,
}) => {
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [checkAllValue, setCheckAllValue] = React.useState(false);

    const handleCheckAll = (value: boolean, event: React.SyntheticEvent) => {
        if (
            (
                (event.target as HTMLElement).parentNode as HTMLElement
            ).className.indexOf('ap-checkbox-label') > -1
        ) {
            return;
        }
        setCheckAllValue(value);
        if (value) {
            if (checkableColumn?.field !== undefined) {
                onSelectedChange?.(
                    data.map((item) => item[checkableColumn.field])
                );
            } else {
                console.error('checkableColumn.field is undefined');
                onSelectedChange?.([]); // Handle the undefined case appropriately
            }
        } else {
            onSelectedChange?.([]);
        }
    };

    useEffect(() => {
        if (selectedIndexs.length === data.length && data.length > 0) {
            setCheckAllValue(true);
        } else {
            setCheckAllValue(false);
        }
    }, [selectedIndexs, data.length]);

    const renderCheckableCell = (row: any, field: string) => {
        if (!(field in row)) return '';

        const onCheckboxChange = (value: boolean) => {
            const field = checkableColumn?.field;
            if (field && typeof field === 'string') {
                const indexValue = row[field];

                if (value) {
                    selectedIndexs.push(indexValue);
                } else {
                    const index = selectedIndexs.indexOf(indexValue);
                    if (index !== -1) {
                        selectedIndexs.splice(index, 1);
                    }
                }
                onSelectedChange?.([...selectedIndexs]);
            } else {
                console.error(
                    'checkableColumn.field is undefined or not a string'
                );
            }
        };

        return (
            <Checkbox
                value={row[field]}
                checked={field ? selectedIndexs.includes(row[field]) : false}
                onChange={(value) => onCheckboxChange(value)}
            >
                <span>
                    {checkableColumn?.renderCell
                        ? checkableColumn.renderCell(row)
                        : row[field]}
                </span>
            </Checkbox>
        );
    };

    const filteredData = useMemo(() => {
        if (!searchQuery) {
            return data;
        }

        // More robust search implementation
        return data.filter((item) => {
            if (!item) return false;

            // Search in all string properties
            return Object.keys(item).some((key) => {
                const value = item[key];
                return (
                    typeof value === 'string' &&
                    value.toLowerCase().includes(searchQuery.toLowerCase())
                );
            });
        });
    }, [searchQuery, data]);

    const actualTotalPages = useMemo(() => {
        return Math.ceil(filteredData.length / pageSize);
    }, [filteredData, pageSize]);

    // Handle page change
    const handlePageChange = (page: number) => {
        onPageChange(page);
    };

    return (
        <div className="listing-table-container">
            {/* Header Section */}
            {title && (
                <div>
                    <h2 className="ap-typography-heading-m title">{title}</h2>
                </div>
            )}

            {/* Tabs Section with Header Buttons */}
            {((tabs && tabs.length > 0) || headerButtons) && (
                <div className="listing-table-header">
                    <div className="listing-table-tabs">
                        {tabs && (
                            <Tabs
                                tabs={tabs}
                                activeTab={activeTab}
                                onTabChange={onTabChange}
                            />
                        )}
                    </div>
                    {headerButtons && (
                        <div className="listing-table-actions">
                            {headerButtons}
                        </div>
                    )}
                </div>
            )}
            {showSearch && (
                <div className="table-search-container">
                    <Search
                        searchType="secondary"
                        placeholder="Search Processes"
                        value={searchQuery}
                        onChange={setSearchQuery}
                        style={{
                            width: '260px',
                            justifySelf: 'right',
                        }}
                    />
                </div>
            )}
            {/* Subheading */}
            {subheading && (
                <div>
                    <p className="ap-typography-body-m fw-bold text-dark">
                        {subheading}
                    </p>
                </div>
            )}

            {/* Table Section */}
            {loading ? (
                <Loading
                    loadingType="circular"
                    indeterminate
                    compact={false}
                    className="mt-2"
                />
            ) : filteredData.length > 0 ? (
                <>
                    <div className="table-wrapper">
                        <Table
                            className={`listing-table ap-table-checkable ${
                                className || ''
                            }`}
                            originalData={filteredData}
                            condensed={condensed}
                            striped={striped}
                            hasTitle={hasTitle}
                            onCheckChange={onCheckChange}
                            tableId={tableId}
                            onRowClick={onRowClick}
                            selectedIndexs={selectedIndexs}
                            currentPage={currentPage}
                            pageSize={pageSize}
                        >
                            {checkableColumn && (
                                <Column
                                    field={checkableColumn.field}
                                    renderCell={renderCheckableCell}
                                >
                                    <Checkbox
                                        checked={checkAllValue}
                                        onChange={(value, event) =>
                                            handleCheckAll(value, event)
                                        }
                                        aria-label={`select all ${checkableColumn.header}`}
                                    >
                                        <span>{checkableColumn.header}</span>
                                    </Checkbox>
                                </Column>
                            )}
                            {columns.map((col, index) => (
                                <Column
                                    key={index}
                                    field={col.field}
                                    style={col.style}
                                    renderCell={col.renderCell}
                                >
                                    {col.header}
                                </Column>
                            ))}
                        </Table>
                    </div>

                    {/* Pagination */}
                    <Pagination
                        total={actualTotalPages}
                        current={currentPage}
                        onPageChange={handlePageChange}
                        style={{ float: 'right', marginTop: '10px' }}
                    />
                </>
            ) : (
                <Table
                    className="text-center"
                    originalData={{
                        message: <p className="m-0">No data available</p>,
                    }}
                >
                    <Column field="message" />
                </Table>
            )}
        </div>
    );
};

export default ListingTable;
